
import { Routes as RouterRoutes, Route, Navigate } from "react-router-dom"
import { Suspense, lazy } from "react"
import { useAuth } from "@/contexts/AuthContext"
import SettingsLayout from "@/components/SettingsLayout"

// Pages
import Index from "@/pages/Index"
import Login from "@/pages/Login"
import Signup from "@/pages/Signup"
import Pricing from "@/pages/Pricing"

// Import settings components directly instead of lazy loading
import { GeneralSettings } from "@/components/settings/GeneralSettings"
import { AISettings } from "@/components/settings/AISettings"
import { ChatInterfaceSettings } from "@/components/settings/ChatInterfaceSettings"

// Lazy loaded pages for better performance
const Dashboard = lazy(() => import("@/pages/Dashboard"))
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"))
const Playground = lazy(() => import("@/pages/Playground"))
const DataSources = lazy(() => import("@/pages/DataSources"))
const APISettings = lazy(() => import("@/pages/APISettings"))
const AccountPreferences = lazy(() => import("@/pages/AccountPreferences"))
const AgentSettings = lazy(() => import("@/pages/AgentSettings"))

export function Routes() {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>
  }

  return (
    <RouterRoutes>
      {/* Public routes */}
      <Route path="/" element={<Index />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/pricing" element={<Pricing />} />
      
      {/* Protected Routes - only accessible when logged in */}
      {user ? (
        <>
          <Route path="/dashboard" element={
            <Suspense fallback={<div>Loading...</div>}>
              <Dashboard />
            </Suspense>
          } />
          
          <Route path="/playground" element={
            <Suspense fallback={<div>Loading...</div>}>
              <Playground />
            </Suspense>
          } />
          
          <Route path="/playground/settings/:chatbotId" element={
            <Suspense fallback={<div>Loading...</div>}>
              <AgentSettings />
            </Suspense>
          } />
          
          <Route path="/data-sources" element={
            <Suspense fallback={<div>Loading...</div>}>
              <DataSources />
            </Suspense>
          } />

          {/* Settings routes with layout */}
          <Route path="/settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="/settings/general" replace />} />
            <Route path="general" element={<GeneralSettings />} />
            <Route path="ai" element={<AISettings />} />
            <Route path="chat" element={<ChatInterfaceSettings chatbotId={null} />} />
            <Route path="api" element={
              <Suspense fallback={<div>Loading...</div>}>
                <APISettings />
              </Suspense>
            } />
            <Route path="account" element={
              <Suspense fallback={<div>Loading...</div>}>
                <AccountPreferences />
              </Suspense>
            } />
          </Route>

          {/* Admin routes */}
          {user.isAdmin && (
            <Route path="/admin" element={
              <Suspense fallback={<div>Loading...</div>}>
                <AdminDashboard />
              </Suspense>
            } />
          )}

          {/* Fallback route for auth users */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </>
      ) : (
        /* Redirect to login if not authenticated */
        <Route path="*" element={<Navigate to="/login" replace />} />
      )}
    </RouterRoutes>
  )
}
